import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import i18n from '../../../../i18next';
import { Col } from 'reactstrap';

import {
    FRONT_END_URL,
    RYFT_PUBLIC_KEY,
    RYFT_GOOGLE_PAY_MERCHANT_ID,
    RYFT_CHECKOUT_SUCCESS_URL,
    RYFT_CHECKOUT_CANCEL_URL,
    STRIPE_ANDROID_REQUIRE_CHROME_FOR_CHECKOUT,
} from '../../../../utils/Constants';
import { getAllUrlParams } from '../../../../utils/CommonUtils';
import { resetStripeBrowserHandoffFlag } from '../../../../utils/stripeBrowserDeepLink';
import { openStripeCheckoutInChromeAndroid } from '../../../../utils/openHttpsInChromeAndroid';
import { refreshStatusBarAfterInAppBrowser } from '../../../../utils/initStatusBar';
import { buildRyftPaymentRequest, createRyftPaymentSession, getRyftAmount, buildRyftError } from '../../../../utils/RyftPaymentUtils';
import {
    placeOrder,
    processingPayment,
    errorProcessingPayment,
    validateCardInput,
    sendEmailReport,
    saveRyftData,
    clearRyftData,
} from '../../../../actions/index';

/** iOS InAppBrowser.close() sometimes returns a Promise that never resolves; do not block placeOrder on it. */
function closeInAppBrowserWithTimeout(browserRef, timeoutMs) {
    const closeRet = browserRef.close();
    if (!closeRet || typeof closeRet.then !== 'function') {
        return Promise.resolve({ outcome: 'sync-or-void' });
    }
    return Promise.race([
        closeRet.then(() => ({ outcome: 'resolved' })).catch((e) => ({ outcome: 'rejected', message: e && e.message })),
        new Promise((resolve) => setTimeout(() => resolve({ outcome: 'timeout' }), timeoutMs)),
    ]);
}

/**
 * Ryft payment for the native Capacitor apps. The Ryft web-SDK wallet buttons can't run inside the
 * embedded WebView, and Ryft has no hosted page, so we open OUR OWN page (public/ryft-payment.html) in the
 * SYSTEM browser (Android Chrome Custom Tabs / iOS InAppBrowser) where card + Apple/Google Pay all work.
 *
 * Mirrors StripeHostedCheckout: create the Ryft session up front, stash {paymentSessionId, amount,
 * manualOrderId} in Redux (saveRyftData), open the page with the clientSecret + storedCards, and on return
 * place the order. Android return comes back via the menuu:// deep link handled in stripeBrowserDeepLink
 * (provider-aware); iOS return is detected here via the InAppBrowser navigation listener.
 */
class RyftHostedCheckout extends Component {
    constructor(props) {
        super(props);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.ryftCapacitorHostedRedirect = this.ryftCapacitorHostedRedirect.bind(this);
        this.completeOrder = this.completeOrder.bind(this);
        /** Prevents double handoff when iOS fires multiple navigation callbacks for the same success URL */
        this._ryftHostedSuccessHandoffDone = false;
    }

    componentDidMount = () => {
        // No inline card form here (payment happens on the browser page), so enable the place-order button.
        this.props.validateCardInput(true);
    }

    // Toggling Cash -> Card on the checkout runs clearPaymentCard() (CLEAR_SELECTED_PAYMENT_CARD), which
    // sets isCardValid=false and greys out the place-order button. This component stays mounted across that
    // toggle (provider 10 always renders it), so componentDidMount won't re-run to re-enable it. Unlike the
    // web inline form, there's no card to validate here — the card is entered on the hosted page AFTER the
    // place-order click — so the button must stay available. Re-assert it whenever isCardValid changes.
    // Mirrors StripeHostedCheckout.
    componentDidUpdate = (prevProps) => {
        if (prevProps.payment.isCardValid !== this.props.payment.isCardValid) {
            this.props.validateCardInput(true);
        }
    }

    componentWillUnmount = () => {
        this.props.validateCardInput(false);
    }

    providerId = () =>
        (this.props.paymentProvider.providerType && this.props.paymentProvider.providerType.id) || 10;

    // Build the public/ryft-payment.html URL. Card declines keep the customer on that page; we only return
    // to the app on success or fatal page error. successUrl/cancelUrl differ per platform (menuu:// deep
    // link on Android, payment-redirect.html on iOS — reused from the Stripe flow).
    buildPageUrl = ({ clientSecret, amount, storedCards, accountId }) => {
        const { selectedRestaurant, brand } = this.props;
        const isAndroid = Capacitor.getPlatform() === 'android';

        const successUrl = isAndroid ? RYFT_CHECKOUT_SUCCESS_URL : `${FRONT_END_URL}/payment-redirect.html?success=true`;
        const cancelUrl = isAndroid ? RYFT_CHECKOUT_CANCEL_URL : `${FRONT_END_URL}/payment-redirect.html?success=false`;

        const qs = new URLSearchParams({
            clientSecret: clientSecret,
            publicKey: RYFT_PUBLIC_KEY,
            amount: String(amount),
            currency: selectedRestaurant.currency || '',
            merchantName: selectedRestaurant.name || 'Menuu',
            merchantCountryCode: (brand && brand.countryCode) || 'GB',
            successUrl: successUrl,
            cancelUrl: cancelUrl,
        });
        if (RYFT_GOOGLE_PAY_MERCHANT_ID) {
            qs.set('googlePayMerchantId', RYFT_GOOGLE_PAY_MERCHANT_ID);
        }
        if (accountId) {
            // Sub-account id (Platform Fee model); the page passes it to createController so the
            // SDK sends it as the 'Account' header on attemptPayment.
            qs.set('accountId', accountId);
        }
        if (storedCards) {
            // Carry the customer's saved cards through to the page for full parity with the inline form.
            // Guard against a pathological card count blowing the URL length: drop them if too long.
            const withCards = `${qs.toString()}&storedCards=${encodeURIComponent(JSON.stringify(storedCards))}`;
            if (withCards.length < 16000) {
                return `${FRONT_END_URL}/ryft-payment.html?${withCards}`;
            }
        }
        return `${FRONT_END_URL}/ryft-payment.html?${qs.toString()}`;
    }

    async handleSubmit(e) {
        e.preventDefault();
        this.props.processingPayment();

        const { customer, selectedRestaurant, shoppingCart, giftCards } = this.props;

        try {
            const requestBody = buildRyftPaymentRequest({ customer, selectedRestaurant, shoppingCart, giftCards });
            const data = await createRyftPaymentSession(requestBody, customer.token);

            if (!data || !data.ryftAuthorizationDTO) {
                this.props.errorProcessingPayment('Payment not ready', 'Payment Failure', this.providerId());
                return;
            }

            const { clientSecret, paymentSessionId, manualOrderId, accountId } = data.ryftAuthorizationDTO;
            const amount = getRyftAmount(shoppingCart, giftCards);

            // Stash the session so the order can be placed on return (analog of saveStripeHostedPaymentData).
            this.props.saveRyftData({ paymentSessionId, amount, manualOrderId });

            const redirectUrl = this.buildPageUrl({ clientSecret, amount, storedCards: data.storedCards, accountId });
            this.ryftCapacitorHostedRedirect(redirectUrl);
        } catch (error) {
            // Surface the friendly userMessage from the backend DTO, falling back to the generic
            // Axios message ("Request failed with status code 500") — never the raw stack trace.
            this.props.errorProcessingPayment(buildRyftError(error), 'Authorize Failure', this.providerId());
            this.props.sendEmailReport(selectedRestaurant.restaurantId, error, 'Ryft createPaymentSession error (app)');
        }
    }

    async ryftCapacitorHostedRedirect(redirectUrl) {
        if (!redirectUrl || !Capacitor.isNativePlatform()) {
            return;
        }

        if (Capacitor.getPlatform() === 'android') {
            // Reset the shared handoff guard before opening; the menuu:// return is handled in
            // stripeBrowserDeepLink (provider-aware: it reads paymentProvider.ryftData).
            this._ryftHostedSuccessHandoffDone = false;
            resetStripeBrowserHandoffFlag();

            if (STRIPE_ANDROID_REQUIRE_CHROME_FOR_CHECKOUT) {
                const opened = await openStripeCheckoutInChromeAndroid(redirectUrl, { toolbarColor: '#1a1a1a' });
                if (!opened) {
                    this.props.errorProcessingPayment(
                        {},
                        'Google Chrome is required for payment. Please install Chrome from the Play Store, then try again.',
                        this.providerId()
                    );
                    this.props.clearRyftData();
                }
            } else {
                await Browser.open({ url: redirectUrl, presentationStyle: 'fullscreen', toolbarColor: '#1a1a1a' });
            }
            return;
        }

        // iOS: embedded InAppBrowser; success/cancel land on payment-redirect.html and are detected via
        // the navigation listener -> completeOrder.
        if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.InAppBrowser) {
            const InAppBrowser = window.Capacitor.Plugins.InAppBrowser;
            this._ryftHostedSuccessHandoffDone = false;

            await InAppBrowser.addListener('browserPageNavigationCompleted', (event) => {
                this.completeOrder(event, InAppBrowser);
            });

            await InAppBrowser.addListener('browserClosed', () => {
                void refreshStatusBarAfterInAppBrowser();
                this.completeOrder(null, null);
            });

            const options = {
                showToolbar: false,
                showURL: false,
                clearCache: false,
                clearSessionCache: false,
                mediaPlaybackRequiresUserAction: false,
                closeButtonText: 'Close',
                toolbarPosition: 0,
                showNavigationButtons: false,
                leftToRight: false,
                customWebViewUserAgent: null,
                android: { allowZoom: false, hardwareBack: true, pauseMedia: true },
                iOS: {
                    allowOverScroll: true,
                    enableViewportScale: false,
                    allowInLineMediaPlayback: false,
                    surpressIncrementalRendering: false,
                    viewStyle: 2,
                    animationEffect: 2,
                    allowsBackForwardNavigationGestures: true,
                },
            };

            await InAppBrowser.openInWebView({ url: redirectUrl, options: options });
        }
    }

    completeOrder = async (event, browserRef) => {
        const ryftData = this.props.paymentProvider.ryftData;

        if (event && event.url && browserRef) {
            // Android returns via the menuu:// deep link (stripeBrowserDeepLink), not here.
            if (Capacitor.getPlatform() === 'android') {
                return;
            }
            if ((event.url).indexOf('success=') !== -1) {
                if (this._ryftHostedSuccessHandoffDone) {
                    return;
                }
                this._ryftHostedSuccessHandoffDone = true;

                if (Capacitor.isNativePlatform()) {
                    browserRef.removeAllListeners();
                }
                await closeInAppBrowserWithTimeout(browserRef, 2500);
                if (Capacitor.isNativePlatform()) {
                    void refreshStatusBarAfterInAppBrowser();
                }

                const isSuccess = getAllUrlParams(event.url).success;
                if (isSuccess === 'true') {
                    if (ryftData) {
                        this.props.placeOrder(false, ryftData);
                        this.props.clearRyftData();
                    }
                } else if (isSuccess === 'false') {
                    this.props.errorProcessingPayment({}, 'Something went wrong.', this.providerId());
                    this.props.clearRyftData();
                }
            }
        } else if (ryftData) {
            // User closed the browser without a success/cancel URL -> treat as cancel.
            if (Capacitor.getPlatform() === 'android') {
                return;
            }
            this.props.errorProcessingPayment({}, i18n.t('screens:shoppingCartActions.cancelledByUser'), this.providerId());
            this.props.clearRyftData();
        }
    }

    render = () => {
        const { primaryFontColor } = this.props.customerThemes.selectedTheme;
        const { cardsInfoMessage } = this.props.styles || {};
        const isMobileView = window.innerWidth <= 865;

        return (
            <Col>
                {/* Hidden form submitted by the external place-order button (form='ryft-pay-form', see
                    ShoppingCartButton.selectAttributeAccordingToProvider case 10). */}
                <form onSubmit={this.handleSubmit} id="ryft-pay-form" hidden={true} />
                {this.props.payment.selectedPaymentType && this.props.payment.selectedPaymentType.id === 1 &&
                    <p
                        style={!this.props.pwaAppRunning && isMobileView ? { ...cardsInfoMessage, color: primaryFontColor, position: 'none', left: 'unset' } :
                            { ...cardsInfoMessage, color: primaryFontColor }}>*{i18n.t('common:alerts.paymentProvidersWarning')}
                    </p>
                }
            </Col>
        );
    }
}

const mapStateToProps = state => {
    return {
        customer: state.customer,
        payment: state.payment,
        brand: state.brand,
        selectedRestaurant: state.selectedRestaurant,
        shoppingCart: state.shoppingCart,
        customerThemes: state.customerThemes,
        pwaAppRunning: state.pwaAppRunning,
        giftCards: state.giftCards,
        promotionPartners: state.promotionPartners,
        paymentProvider: state.paymentProvider,
    };
};

export default connect(mapStateToProps, {
    placeOrder,
    processingPayment,
    errorProcessingPayment,
    validateCardInput,
    sendEmailReport,
    saveRyftData,
    clearRyftData,
})(RyftHostedCheckout);